/**
 * Cascade Coil — when the armed shot lands, every color it touches loses
 * N random gears elsewhere on the board (N scales with level; Mk. V clears
 * each touched color outright). No match is required. The floating-drop
 * pass in the engine then brings down whatever those gears were holding.
 *
 * Deterministic and RNG-driven, so both the engine and the renderer's
 * shot-prediction path (via a forked PRNG) resolve identical targets.
 *
 * MIRRORED in supabase/functions/_shared/engine-replay.ts — keep in sync.
 */

import type { PRNG } from '../../rng.js';
import type { BoardState, HexCoord } from '../../types.js';
import { ALL_BUBBLE_COLORS, BubbleColor, getBubble } from '../../types.js';
import { coordToKey, hexNeighbors } from '../../grid/hex.js';
import { MutationType } from '../registry.js';
import { CASCADE_COIL_CLEAR_ALL, enhancementMagnitude } from '../levels.js';

export interface CascadeCoilTargets {
  /** Colors adjacent to the landed shot, in enum order. */
  readonly touchedColors: BubbleColor[];
  /** Gears removed by the coil, grouped in `touchedColors` order. */
  readonly removed: HexCoord[];
  /** Number of touched colors that actually lost at least one gear. */
  readonly colorsHit: number;
}

/**
 * Resolve the coil's targets.
 *
 * @param boardAtLanding  board before any pop, with the shot already placed at `landing`
 * @param boardAfterPop   board after the primary cluster pop (may equal boardAtLanding)
 * @param landing         where the shot snapped
 * @param level           enhancement level (Mk. I..V)
 * @param rng             engine PRNG (or a fork for prediction)
 */
export function pickCascadeCoilTargets(
  boardAtLanding: BoardState,
  boardAfterPop: BoardState,
  landing: HexCoord,
  level: number | undefined,
  rng: PRNG,
): CascadeCoilTargets {
  const magnitude = enhancementMagnitude(MutationType.CascadeCoil, level);
  const clearAll = magnitude === CASCADE_COIL_CLEAR_ALL;

  // Colors of the gears the shot touches, plus the touched cells themselves
  // (protected from random removal so the "other gears" wording holds).
  const touchedSet = new Set<BubbleColor>();
  const protectedKeys = new Set<string>([coordToKey(landing)]);
  for (const n of hexNeighbors(landing)) {
    const b = getBubble(boardAtLanding, n);
    if (!b || b.isObstacle || b.isWild) continue;
    touchedSet.add(b.color);
    protectedKeys.add(coordToKey(n));
  }
  const touchedColors = ALL_BUBBLE_COLORS.filter(c => touchedSet.has(c));

  const removed: HexCoord[] = [];
  let colorsHit = 0;
  for (const color of touchedColors) {
    const candidates: { key: string; coord: HexCoord }[] = [];
    for (const [key, b] of boardAfterPop.bubbles) {
      if (b.color !== color || b.isObstacle || b.isWild) continue;
      if (!clearAll && protectedKeys.has(key)) continue;
      candidates.push({ key, coord: b.coord });
    }
    if (candidates.length === 0) continue;
    // Sort by key so the draw never depends on Map insertion history.
    candidates.sort((a, b) => (a.key < b.key ? -1 : a.key > b.key ? 1 : 0));

    if (clearAll) {
      for (const c of candidates) removed.push(c.coord);
      colorsHit++;
      continue;
    }

    const n = Math.min(magnitude, candidates.length);
    for (let i = 0; i < n; i++) {
      const j = i + rng.nextInt(0, candidates.length - 1 - i);
      const tmp = candidates[i]!;
      candidates[i] = candidates[j]!;
      candidates[j] = tmp;
      removed.push(candidates[i]!.coord);
    }
    colorsHit++;
  }

  return { touchedColors, removed, colorsHit };
}
